import { ActionIcon, Alert, CopyButton, Group, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconCopy, IconKey } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export interface HmacSecretRevealProps {
  secret: string;
}

/**
 * One-time HMAC secret reveal box. Shown after create / rotate-secret.
 *
 * Renders a yellow alert + copy-to-clipboard button. The component never
 * persists the secret anywhere — that's the parent's responsibility.
 */
export function HmacSecretReveal({ secret }: HmacSecretRevealProps) {
  const { t } = useTranslation();

  return (
    <Alert
      color="yellow"
      icon={<IconKey size={18} />}
      title={t('integrations.secret.title')}
      variant="light"
    >
      <Text size="sm" mb="xs">
        {t('integrations.secret.warning')}
      </Text>
      <Group gap="xs" wrap="nowrap">
        <Text
          ff="monospace"
          size="sm"
          style={{
            wordBreak: 'break-all',
            background: 'var(--mantine-color-gray-1)',
            padding: '4px 8px',
            borderRadius: 4,
            flex: 1,
          }}
        >
          {secret}
        </Text>
        <CopyButton value={secret}>
          {({ copied, copy }) => (
            <Tooltip
              label={copied ? t('integrations.secret.copied') : t('integrations.secret.copy')}
            >
              <ActionIcon variant="light" onClick={copy} size="lg">
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
    </Alert>
  );
}
